import React, { useState, useEffect } from "react";
import { 
  XMarkIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
  AcademicCapIcon
} from "@heroicons/react/24/outline";
import { ChevronRightIcon, StarIcon } from "@heroicons/react/24/solid";

interface HelpCenterProps {
  onClose?: () => void;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  helpful: number;
  tags: string[];
}

interface HelpCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  articleCount: number;
  color: string;
}

const helpCategories: HelpCategory[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    icon: <AcademicCapIcon className="w-6 h-6" />,
    description: "Basic setup and onboarding guides",
    articleCount: 12,
    color: "blue"
  },
  {
    id: "account-management",
    name: "Account Management",
    icon: <WrenchScrewdriverIcon className="w-6 h-6" />,
    description: "User accounts, billing, and settings",
    articleCount: 8,
    color: "green"
  },
  {
    id: "features",
    name: "Platform Features",
    icon: <LightBulbIcon className="w-6 h-6" />,
    description: "How to use specific platform features",
    articleCount: 25,
    color: "purple"
  },
  {
    id: "troubleshooting",
    name: "Troubleshooting",
    icon: <ExclamationTriangleIcon className="w-6 h-6" />,
    description: "Common issues and solutions",
    articleCount: 15,
    color: "yellow"
  },
  {
    id: "integrations",
    name: "Integrations",
    icon: <DocumentTextIcon className="w-6 h-6" />,
    description: "Third-party integrations and APIs",
    articleCount: 18,
    color: "indigo"
  },
  {
    id: "best-practices",
    name: "Best Practices",
    icon: <InformationCircleIcon className="w-6 h-6" />,
    description: "Tips and optimization strategies",
    articleCount: 10,
    color: "teal"
  }
];

const faqs: FAQ[] = [
  {
    id: "1",
    question: "How do I create my first job requisition?",
    answer: "To create a job requisition, navigate to the Requisitions page and click 'Create New Requisition'. Fill in the job details including title, description, requirements, and compensation. Once saved, the requisition will be available for candidate sourcing.",
    category: "getting-started",
    helpful: 24,
    tags: ["requisitions", "jobs", "create"]
  },
  {
    id: "2", 
    question: "How does the AI-powered candidate matching work?",
    answer: "Our AI matching system analyzes job requirements and candidate profiles using natural language processing and machine learning. It considers skills, experience, location, and other factors to provide relevance scores and rank candidates by fit.",
    category: "features",
    helpful: 31,
    tags: ["AI", "matching", "search", "candidates"]
  },
  {
    id: "3",
    question: "Can I integrate Metatalio with my existing ATS?",
    answer: "Yes, Metatalio supports integration with popular ATS platforms including Workday, BambooHR, Greenhouse, and others. Contact our support team to set up your specific integration.",
    category: "integrations",
    helpful: 19,
    tags: ["ATS", "integration", "sync"]
  },
  {
    id: "4",
    question: "How do I invite team members to my organization?",
    answer: "Go to Settings > Users and click 'Invite User'. Enter their email address, select their role, and send the invitation. They'll receive an email to set up their account.",
    category: "account-management",
    helpful: 27,
    tags: ["users", "invite", "team", "roles"]
  },
  {
    id: "5",
    question: "What should I do if candidate search is not returning results?",
    answer: "Try broadening your search criteria, check spelling, use synonyms for skills, or remove some filters. Ensure your search terms are not too specific. You can also use our natural language search feature.",
    category: "troubleshooting",
    helpful: 16,
    tags: ["search", "no results", "candidates", "filters"]
  },
  {
    id: "6",
    question: "How can I optimize my recruitment process?",
    answer: "Use our analytics dashboard to identify bottlenecks, set up automated workflows, create role templates for consistency, and leverage AI insights for better candidate targeting.",
    category: "best-practices", 
    helpful: 22,
    tags: ["optimization", "analytics", "workflow", "efficiency"]
  },
  {
    id: "7",
    question: "What permissions do I need to access analytics?",
    answer: "You need the ANALYTICS_READ permission to view reports and dashboards. Contact your tenant administrator to request access if you don't see the Analytics menu.",
    category: "account-management",
    helpful: 14,
    tags: ["permissions", "analytics", "access", "roles"]
  },
  {
    id: "8",
    question: "How do I export candidate data?",
    answer: "Navigate to the Candidates page, apply any desired filters, then click the 'Export' button. You can choose between CSV, Excel, or PDF formats. Large exports may be emailed to you.",
    category: "features",
    helpful: 18,
    tags: ["export", "data", "candidates", "CSV", "Excel"]
  }
];

export const HelpCenter: React.FC<HelpCenterProps> = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, number>>({});

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = searchQuery === "" || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === null || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleHelpfulVote = (faqId: string) => {
    setHelpfulVotes(prev => ({
      ...prev,
      [faqId]: (prev[faqId] || 0) + 1
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 max-w-7xl mx-auto my-4 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <QuestionMarkCircleIcon className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Help Center</h1>
              <p className="text-purple-100">Find answers and get support</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-200" />
          <input
            type="text"
            placeholder="Search for help articles and FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-white/30"
          />
        </div>
      </div>

      <div className="flex h-[calc(100vh-200px)] overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-6">
            {/* Quick Contact */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-3">Need immediate help?</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  <span>support@metatalio.com</span>
                </div>
                <div className="flex items-center">
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  <span>+1 (555) 123-4567</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  <span>Mon-Fri, 9AM-6PM EST</span>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Browse by Category
              </h2>
              <button
                onClick={() => setSelectedCategory(null)}
                className={`w-full mb-2 p-3 rounded-lg text-left transition-colors ${
                  selectedCategory === null
                    ? "bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                }`}
              >
                <div className="font-medium">All Categories</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {faqs.length} articles
                </div>
              </button>
              
              <div className="space-y-2">
                {helpCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                      selectedCategory === category.id
                        ? "bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100"
                        : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    <div className={`text-${category.color}-500`}>
                      {category.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{category.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {category.articleCount} articles
                      </div>
                    </div>
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            <div className="max-w-4xl">
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {selectedCategory 
                    ? helpCategories.find(c => c.id === selectedCategory)?.name || "All Articles"
                    : "Frequently Asked Questions"
                  }
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {selectedCategory 
                    ? helpCategories.find(c => c.id === selectedCategory)?.description
                    : `${filteredFAQs.length} helpful articles and answers`
                  }
                </p>
              </div>

              {/* FAQ List */}
              <div className="space-y-4">
                {filteredFAQs.length === 0 ? (
                  <div className="text-center py-12">
                    <QuestionMarkCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      No articles found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Try adjusting your search terms or browse a different category
                    </p>
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedCategory(null);
                      }}
                      className="text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  filteredFAQs.map((faq) => (
                    <div
                      key={faq.id}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                        className="w-full p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                              {faq.question}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                              <span className="flex items-center">
                                <StarIcon className="w-4 h-4 text-yellow-400 mr-1" />
                                {faq.helpful + (helpfulVotes[faq.id] || 0)} helpful
                              </span>
                              <span>
                                {helpCategories.find(c => c.id === faq.category)?.name}
                              </span>
                            </div>
                          </div>
                          <ChevronRightIcon 
                            className={`w-5 h-5 text-gray-400 transform transition-transform ${
                              expandedFAQ === faq.id ? "rotate-90" : ""
                            }`}
                          />
                        </div>
                      </button>
                      
                      {expandedFAQ === faq.id && (
                        <div className="px-6 pb-6">
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                          
                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {faq.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>

                          {/* Helpful Actions */}
                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Was this helpful?
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleHelpfulVote(faq.id)}
                                className="px-3 py-1 text-sm bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                              >
                                👍 Yes
                              </button>
                              <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                👎 No
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Contact Support */}
              <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-8 text-center">
                <ChatBubbleLeftRightIcon className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Still need help?
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2" />
                    Start Live Chat
                  </button>
                  <button className="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors">
                    <EnvelopeIcon className="w-5 h-5 mr-2" />
                    Email Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};